import PropTypes from 'prop-types';
import type { StyledComponent } from 'styled-components';
import styled from 'styled-components';
import type { ResponsiveValue, WidthProps } from 'styled-system';
import { color, variant, width } from 'styled-system';
import type { LiteralUnion } from 'type-fest';

// import { darken, hsla, parseToHsl } from 'polished';
import type { BorderProps, CommonProps, TypographyProps } from './constants';
import { BORDER, COMMON, TYPOGRAPHY } from './constants';
import type { Theme } from './theme';
import theme from './theme';
// const buttonBgDarkenPercentage = 0.06;

type Variant = 'normal' | 'big';

type ButtonProps = CommonProps &
  BorderProps &
  WidthProps<Theme> & {
    variant?: ResponsiveValue<LiteralUnion<'normal' | 'big', string>, Theme>;
  };

// eslint-disable-next-line @typescript-eslint/ban-types
const buttonSize = variant<object, Variant>({
  variants: {
    normal: {
      padding: '8px 16px',
      fontSize: theme.fontSizes.s,
    },
    big: {
      padding: '12px 18px',
      fontSize: theme.fontSizes.m,
    },
  },
});

const Button = styled.button<ButtonProps>`
  -moz-osx-font-smoothing: grayscale;
  -webkit-font-smoothing: antialiased;
  text-decoration: none;
  color: ${theme.colors.black};
  display: inline-block;
  font-weight: bold;
  text-align: center; /* Need it for anchor button */
  line-height: 1;
  border-radius: ${theme.radii[1]};
  border-style: solid;
  border-width: 2px;
  border-color: ${theme.colors.gray.medium};
  background-color: transparent;
  -webkit-tap-highlight-color: hsla(0, 0%, 0%, 0);
  -webkit-appearance: initial !important; /* to override when buttons have type=button */
  transition: background ${theme.transitionDuration.fast} ease, border ${theme.transitionDuration.fast} ease;
  cursor: pointer;
  user-select: none;

  &:focus {
    text-decoration: underline;
    outline: 0;
  }
  &:hover {
    border-color: ${theme.colors.gray.dark};
  }
  &:active {
    background: ${theme.colors.karl.translucent};
    color: ${theme.colors.black};
    text-decoration: none;
    border-color: ${theme.colors.gray.dark};
  }
  &:disabled,
  &:disabled:hover {
    background: ${theme.colors.gray.light};
    color: ${theme.colors.gray.medium};
    filter: none;
    cursor: default;
    border-color: ${theme.colors.gray.light};
  }
  ${BORDER};
  ${COMMON};
  ${width};
  ${buttonSize};
`;

Button.defaultProps = {
  theme,
  variant: 'normal',
};

Button.propTypes = {
  children: PropTypes.node,
  disabled: PropTypes.bool,
  as: PropTypes.oneOfType([PropTypes.oneOf(['button', 'a', 'input']), PropTypes.elementType]),
  onClick: PropTypes.func,
  variant: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  theme: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  ...COMMON.propTypes,
  ...(width as any).propTypes,
};

const ActionButton = styled(Button)`
  background: ${theme.colors.red.brand};
  color: ${theme.colors.white};
  border-color: ${theme.colors.red.brand};
  &:hover {
    color: ${theme.colors.white}; /* Needed to override <a> tag hover */
    background: #dc1d13; /*darken(buttonBgDarkenPercentage, theme.colors.red.brand) */
    border-color: #dc1d13; /*darken(buttonBgDarkenPercentage, theme.colors.red.brand) */
  }
  &:active {
    color: ${theme.colors.red.brand};
    border-color: ${theme.colors.red.brand};
    background: ${theme.colors.white};
  }
`;

const ActionInvertedButton = styled(Button)`
  border-color: ${theme.colors.white};
  background: ${theme.colors.white};
  color: ${theme.colors.red.brand};
  &:hover {
    border-color: transparent;
    color: #dc1d13; /*darken(buttonBgDarkenPercentage, theme.colors.red.brand) */
  }
  &:active {
    color: ${theme.colors.white};
    background: ${theme.colors.red.brand};
    border-color: ${theme.colors.red.brand};
  }
`;

const OutlineButton = styled(Button)`
  border-color: ${theme.colors.red.brand};
  background: ${theme.colors.white};
  color: ${theme.colors.red.brand};
  &:hover {
    border-color: #dc1d13; /*darken(buttonBgDarkenPercentage, theme.colors.red.brand) */
    color: #dc1d13; /*darken(buttonBgDarkenPercentage, theme.colors.red.brand) */
  }
  &:active {
    background: ${theme.colors.red.brand};
    color: ${theme.colors.white};
    border-color: ${theme.colors.red.brand};
  }
`;

const OutlineInvertedButton = styled(Button)`
  border-color: ${theme.colors.white};
  background: none;
  color: ${theme.colors.white};
  &:hover {
    color: ${theme.colors.white}; /* Needed to override <a> tag hover */
    border-color: ${theme.colors.karl.theFog};
    background: hsla(0, 0%, 0%, 0.1); /* hsla(parseToHsl(theme.colors.black).hue, 0, 0, 0.1) */
  }
  &:active {
    background: ${theme.colors.white};
    color: ${theme.colors.red.brand};
    border-color: ${theme.colors.white};
  }
`;

type TextButtonProps = TypographyProps;

const TextButton = styled(Button)<TextButtonProps>`
  -webkit-font-smoothing: auto;
  background: none;
  border-color: transparent;
  color: ${theme.colors.red.brand};
  text-decoration: underline;
  &:hover {
    color: ${theme.colors.black};
    border-color: transparent;
  }
  &:focus {
    outline: 2px solid ${theme.colors.gray.light};
    outline-color: ${theme.colors.gray.lightTranslucent};
  }
  &:active {
    color: ${theme.colors.black};
    background: none;
    text-decoration: underline;
    border-color: transparent;
    outline: none;
  }
  ${color};
  ${TYPOGRAPHY};
`;

TextButton.defaultProps = {
  theme,
};

const FacebookButton = styled(Button)`
  background: ${theme.colors.blue.fb};
  color: ${theme.colors.white};
  border-color: ${theme.colors.blue.fb};
  &:hover {
    color: ${theme.colors.white}; /* Needed to override <a> tag hover */
    background: #2f5587; /* darken(buttonBgDarkenPercentage, theme.colors.blue.fb) */
    border-color: #2f5587; /* darken(buttonBgDarkenPercentage, theme.colors.blue.fb) */
  }
  &:active {
    color: ${theme.colors.blue.fb};
    background: ${theme.colors.white};
    border-color: ${theme.colors.blue.fb};
  }
`;

const TwitterButton = styled(Button)`
  background: ${theme.colors.blue.twitter};
  color: ${theme.colors.white};
  border-color: ${theme.colors.blue.twitter};
  &:hover {
    color: ${theme.colors.white}; /* Needed to override <a> tag hover */
    background: #1094e5; /* darken(buttonBgDarkenPercentage, theme.colors.blue.twitter) */
    border-color: #1094e5; /* darken(buttonBgDarkenPercentage, theme.colors.blue.twitter) */
  }
  &:active {
    color: ${theme.colors.blue.twitter};
    background: ${theme.colors.white};
    border-color: ${theme.colors.blue.twitter};
  }
`;

const GoogleButton = styled(Button)`
  background: #4285f4;
  color: ${theme.colors.white};
  border-color: #4285f4;
  &:hover {
    color: ${theme.colors.white};
    background: #3174e3;
    border-color: #3174e3;
  }
  &:active {
    color: ${theme.colors.white};
    background: #3174e3;
    border-color: #3174e3;
  }
`;

Button.displayName = 'Button';
ActionButton.displayName = 'ActionButton';
ActionInvertedButton.displayName = 'ActionInvertedButton';
OutlineButton.displayName = 'OutlineButton';
OutlineInvertedButton.displayName = 'OutlineInvertedButton';
TextButton.displayName = 'TextButton';
FacebookButton.displayName = 'FacebookButton';
TwitterButton.displayName = 'TwitterButton';
GoogleButton.displayName = 'GoogleButton';

export { Button };
export { ActionButton };
export { ActionInvertedButton };
export { OutlineButton };
export { OutlineInvertedButton };
export { TextButton };
export { FacebookButton };
export { TwitterButton };
export { GoogleButton };

export type ButtonComponent = StyledComponent<'button', any, ButtonProps>;
